import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Flame, TrendingUp, Lightbulb, FileDown, Crown, Shield, LogOut,
  ChevronRight, Download, Loader2,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Navbar } from "@/components/Navbar";
import { PageTransition } from "@/components/PageTransition";
import { IosInstallSheet } from "@/components/InstallPrompt";
import { usePwaInstall } from "@/lib/pwa";
import { api } from "@/lib/api";
import { toast } from "sonner";

function Ring({ percent }) {
  const r = 30, c = 2 * Math.PI * r;
  const off = c - (Math.min(100, Math.max(0, percent)) / 100) * c;
  return (
    <div className="relative h-20 w-20 shrink-0">
      <svg className="h-20 w-20 -rotate-90" viewBox="0 0 68 68">
        <circle cx="34" cy="34" r={r} className="stroke-secondary" strokeWidth="6" fill="none" />
        <circle cx="34" cy="34" r={r} className="stroke-primary transition-all duration-1000 ease-out"
          strokeWidth="6" fill="none" strokeLinecap="round" strokeDasharray={c} strokeDashoffset={off} />
      </svg>
      <span data-testid="me-progress-percent" className="absolute inset-0 grid place-items-center font-heading font-bold">{percent}%</span>
    </div>
  );
}

function Row({ icon: Icon, color, label, sub, onClick, testid, right }) {
  return (
    <button data-testid={testid} onClick={onClick}
      className="tap flex items-center gap-3.5 w-full rounded-2xl px-4 py-3.5 text-left bg-card border border-border hover:border-primary/30 active:scale-[0.99] transition-all">
      <span className={`grid place-items-center h-10 w-10 rounded-xl bg-secondary/60 shrink-0 ${color}`}>
        <Icon className="h-5 w-5" strokeWidth={2.3} />
      </span>
      <span className="flex-1 min-w-0">
        <span className="block font-semibold leading-tight">{label}</span>
        <span className="block text-xs text-muted-foreground truncate mt-0.5">{sub}</span>
      </span>
      {right || <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />}
    </button>
  );
}

export default function Me() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [path, setPath] = useState(null);
  const [notesCount, setNotesCount] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [iosOpen, setIosOpen] = useState(false);
  const install = usePwaInstall();

  useEffect(() => {
    api.get("/path").then(({ data }) => setPath(data)).catch(() => {});
    api.get("/pro/notes/all").then(({ data }) => setNotesCount(data.count)).catch(() => setNotesCount(0));
  }, []);

  const exportNotes = async () => {
    setExporting(true);
    try {
      const { data } = await api.get("/pro/notes/all");
      if (!data.notes.length) { toast.info("No notes yet — take notes inside any lesson first."); return; }
      const md = ["# My AI Pathshaalaa notes", ""].concat(
        data.notes.map((n) => `## ${n.lesson_title}\n*${n.channel}*\n\n${n.notes}\n`)
      ).join("\n");
      const blob = new Blob([md], { type: "text/markdown" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = "ai-pathshaalaa-notes.md";
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("Notes downloaded!");
    } catch {
      toast.error("Could not export notes.");
    } finally {
      setExporting(false);
    }
  };

  const doInstall = async () => {
    if (install.canInstall) await install.promptInstall();
    else if (install.ios) setIosOpen(true);
  };

  const handleLogout = async () => { navigate("/", { replace: true }); await logout(); };
  const initials = (user?.name || user?.email || "U").trim().split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  const isPro = user?.is_subscribed || user?.role === "admin";

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <PageTransition className="max-w-2xl mx-auto px-4 sm:px-6 pt-24 pb-24" data-testid="me-page">
        {/* profile */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="flex items-center gap-4">
          <span className="grid place-items-center h-16 w-16 rounded-2xl bg-gradient-to-br from-indigo-100 to-sky-100 text-indigo-700 font-heading font-bold text-xl ring-2 ring-border">
            {initials}
          </span>
          <div className="min-w-0 flex-1">
            <h1 className="font-heading font-extrabold text-2xl truncate">{user?.name || "Learner"}</h1>
            <p className="text-sm text-muted-foreground truncate">{user?.email}</p>
            <div className="flex items-center gap-2 mt-1.5 flex-wrap">
              {path?.archetype_label && (
                <span className="text-xs font-medium px-2.5 py-0.5 rounded-full bg-secondary text-muted-foreground">{path.archetype_label}</span>
              )}
              {isPro && (
                <span data-testid="me-pro-badge" className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
                  <Crown className="h-3 w-3" /> Pro
                </span>
              )}
            </div>
          </div>
        </motion.div>

        {/* progress + streak */}
        {path && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.06 }}
            className="mt-6 rounded-2xl border border-border bg-card p-5 flex items-center gap-5" data-testid="me-progress-card">
            <Ring percent={path.percent} />
            <div className="min-w-0 flex-1">
              <p className="font-heading font-bold">{path.completed_count}/{path.total} lessons</p>
              <p className="text-xs text-muted-foreground mt-0.5">{path.finished ? "Path complete 🏆" : "of your personalized path"}</p>
              <p data-testid="me-streak" className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-1 rounded-full bg-orange-100 text-orange-600">
                <Flame className={`h-4 w-4 ${path.streak > 0 ? "fill-orange-500" : ""}`} />
                {path.streak} day streak{path.streak > 0 ? " — keep it alive!" : " — watch a lesson to start one"}
              </p>
            </div>
          </motion.div>
        )}

        {/* my stuff */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.12 }} className="mt-6 flex flex-col gap-2">
          <p className="font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground px-1 pb-1">My learning</p>
          <Row icon={TrendingUp} color="text-emerald-600" label="Progress analytics" sub="Charts, quiz scores & topic mastery" testid="me-analytics" onClick={() => navigate("/analytics")} />
          <Row icon={Lightbulb} color="text-pink-600" label="Prompt library" sub="Ready-to-use AI prompts, organised in packs" testid="me-prompts" onClick={() => navigate("/prompts")} />
          <Row icon={FileDown} color="text-sky-600" label="Export my notes" testid="me-notes-download"
            sub={notesCount === null ? "…" : notesCount > 0 ? `${notesCount} lesson note${notesCount !== 1 ? "s" : ""} → download as Markdown` : "No notes yet — take notes inside lessons"}
            onClick={exportNotes}
            right={exporting ? <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /> : <Download className="h-4 w-4 text-muted-foreground shrink-0" />} />

          <p className="font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground px-1 pb-1 pt-4">Account</p>
          <Row icon={Crown} color="text-primary" label="My subscription" sub="Plan, billing & payment history" testid="me-account" onClick={() => navigate("/account")} />
          {user?.role === "admin" && (
            <Row icon={Shield} color="text-indigo-600" label="Admin Panel" sub="Command center" testid="me-admin" onClick={() => navigate("/admin")} />
          )}
          {!install.standalone && (install.canInstall || install.ios) && (
            <Row icon={Download} color="text-violet-600" label="Install the app" sub="Add to Home Screen — full-screen, app-like" testid="me-install" onClick={doInstall} />
          )}

          <button data-testid="me-logout" onClick={handleLogout}
            className="tap mt-4 flex items-center justify-center gap-2 rounded-2xl px-4 py-3.5 bg-red-50 text-red-600 font-semibold active:scale-[0.99] transition-transform">
            <LogOut className="h-5 w-5" /> Log out
          </button>
        </motion.div>
      </PageTransition>
      <IosInstallSheet open={iosOpen} onOpenChange={setIosOpen} />
    </div>
  );
}
